import React from 'react';
import clsx from 'clsx';
import styles from './BankingOverview.module.scss';
import type { ModuleDefinition } from './moduleRegistry';
import type { AggBankingProps, BankingOverviewAccount, ReportData } from '../data/types';
import {
    DASH,
    REPORT_ANCHOR_ATTR,
    REPORT_MODULE_ID,
    SUB_OPTION_INDIVIDUAL_BANKS_ID,
    bankAccountSubId,
    reportAnchorId,
    subOptionOn,
} from '../constants';
import { ScreenSection, ScreenDivider } from '../primitives/ScreenPrimitives';
import { coverFonts, overviewColors, ptToPx } from '../data/designTokens';
import { fmtDate } from './formatUtils';
import { ScreenAggBankingBody, aggBankingSubtitle } from './AggregatedBanking';

// ════════════════════════════════════════════════════════════════
//                      D E S I G N   T O K E N S
//
// Banking Overview as a 6-column table — Figma 157:5646.
// Replaces the previous 2-up card grid; the Figma now puts every account on
// its own row with a totals footer pinned at the bottom.
// ════════════════════════════════════════════════════════════════

const bankingTable = {
    radius: 8,
    border: 1,
    borderColor: overviewColors.border,
    rowDivider: 0.5,
    rowDividerColor: overviewColors.border,

    headerBg: '#F7F8F9',
    headerHeight: 30, // 44px in Figma → 33pt; trimmed to keep page density honest
    headerSize: 7.5, // 9px Figma → 7.5pt
    headerColor: '#6B7080',
    headerFontWeight: 600,

    rowHeight: 26, // 40px in Figma → 30pt; trimmed for PDF readability
    rowBg: '#FFFFFF',
    cellPadX: 8, // 12px in Figma → 9pt; trimmed slightly to fit A4 width
    cellPadY: 6,
    bankNameSize: 8.5,
    bankNameColor: overviewColors.ink,
    bankNameFontWeight: 600,
    cellSize: 8.5,
    cellColor: '#4A5463',
    cellMutedColor: overviewColors.inkMuted,
    negColor: '#DB2626',

    // Footer row — bigger top border, neutral grey body. Figma uses an orange
    // (#EB9424) 2pt rule but our page system already has accents on every
    // section — using orange here would clash with the section eyebrow on the
    // facing page. Falling back to ink-soft 1pt for now; revisit if the Figma
    // intent of the orange rule is "this is the totals row" rather than
    // "use orange specifically".
    footerBg: '#F7F8F9',
    footerBorderTop: 1.5,
    footerBorderColor: overviewColors.inkSoft,
    footerSize: 8.5,
    footerColor: overviewColors.ink,
    footerFontWeight: 600,

    // Column flex weights (proportional to Figma frame widths 120/130/105/105/115/97.75).
    // Sum is preserved so each cell gets the same relative slice on any page width.
    flex: {
        bankName: 18,
        accountNumber: 19,
        closingBalance: 16,
        closingDate: 16,
        odLimit: 17,
        ltmTxn: 14,
    },
} as const;

const SF = coverFonts.screen;

// Indian numbering system. Negative balances render in parentheses with red tint.
const fmtINRSigned = (n?: number): { text: string; negative: boolean } => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return { text: DASH, negative: false };
    const abs = Math.abs(n);
    const formatted = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(abs);
    return { text: n < 0 ? `(${formatted})` : formatted, negative: n < 0 };
};

const fmtINR = (n?: number): string => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return DASH;
    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);
};

const sumNumbers = (xs: Array<number | undefined>): number =>
    xs.reduce<number>((acc, n) => acc + (typeof n === 'number' && Number.isFinite(n) ? n : 0), 0);

type Totals = {
    closingBalance?: number;
    odSanctionLimit?: number;
    transactionsLTM?: number;
};

const computeTotals = (accounts: BankingOverviewAccount[]): Totals => {
    const hasAny = (key: keyof BankingOverviewAccount) =>
        accounts.some((a) => typeof a[key] === 'number' && Number.isFinite(a[key] as number));
    return {
        closingBalance: hasAny('closingBalance') ? sumNumbers(accounts.map((a) => a.closingBalance)) : undefined,
        odSanctionLimit: hasAny('odSanctionLimit') ? sumNumbers(accounts.map((a) => a.odSanctionLimit)) : undefined,
        transactionsLTM: hasAny('transactionsLTM') ? sumNumbers(accounts.map((a) => a.transactionsLTM)) : undefined,
    };
};

// ════════════════════════════════════════════════════════════════
//                            S C R E E N
// ════════════════════════════════════════════════════════════════

const ScreenHeaderCell: React.FC<{ flex: number; align?: 'left' | 'right'; children: React.ReactNode }> = ({
    flex,
    align = 'left',
    children,
}) => (
    <div
        style={{
            flex,
            minWidth: 0,
            paddingLeft: ptToPx(bankingTable.cellPadX),
            paddingRight: ptToPx(bankingTable.cellPadX),
            paddingTop: ptToPx(bankingTable.cellPadY),
            paddingBottom: ptToPx(bankingTable.cellPadY),
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
    >
        <span
            style={{
                fontSize: ptToPx(bankingTable.headerSize),
                fontFamily: SF.sans,
                fontWeight: bankingTable.headerFontWeight,
                color: bankingTable.headerColor,
                textAlign: align,
                whiteSpace: 'pre-line',
            }}
        >
            {children}
        </span>
    </div>
);

const ScreenCell: React.FC<{
    flex: number;
    align?: 'left' | 'right';
    mono?: boolean;
    bold?: boolean;
    color?: string;
    children: React.ReactNode;
}> = ({ flex, align = 'left', mono, bold, color, children }) => (
    <div
        style={{
            flex,
            minWidth: 0,
            paddingLeft: ptToPx(bankingTable.cellPadX),
            paddingRight: ptToPx(bankingTable.cellPadX),
            paddingTop: ptToPx(bankingTable.cellPadY),
            paddingBottom: ptToPx(bankingTable.cellPadY),
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        }}
    >
        <span
            style={{
                fontSize: ptToPx(bankingTable.cellSize),
                fontFamily: mono ? SF.mono : SF.sans,
                fontWeight: bold ? 600 : 400,
                color: color ?? bankingTable.cellColor,
                textAlign: align,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    </div>
);

const BankAnalysisBlock: React.FC<{ title: string; subtitle?: string; d: AggBankingProps; anchor?: string }> = ({
    title,
    subtitle,
    d,
    anchor,
}) => (
    <div className={styles.AnalysisBlock} {...(anchor ? { [REPORT_ANCHOR_ATTR]: anchor } : {})}>
        <ScreenDivider />
        <div className={styles.AnalysisBody}>
            <div className={clsx(styles.AnalysisTitle, { [styles.AccountHeading]: !subtitle })}>{title}</div>
            {subtitle ? <div className={styles.AnalysisSubtitle}>{subtitle}</div> : null}
            <ScreenAggBankingBody d={d} />
        </div>
    </div>
);

const ScreenBankingOverview: React.FC<{ data: ReportData; index: string; options?: Record<string, boolean> }> = ({
    data,
    index,
    options,
}) => {
    const overview = data.bankingOverview;
    if (!overview || overview.accounts.length === 0) return null;
    const totals = computeTotals(overview.accounts);
    const totalsClosing = fmtINRSigned(totals.closingBalance);
    const totalsOd = fmtINRSigned(totals.odSanctionLimit);
    const showIndividual = subOptionOn(options, SUB_OPTION_INDIVIDUAL_BANKS_ID);
    const accountsToPrint = showIndividual
        ? (data.bankAccountAnalyses ?? []).filter((a) => subOptionOn(options, bankAccountSubId(a.accountNumber)))
        : [];
    return (
        <ScreenSection kicker="Banking" title="Banking Overview" index={index}>
            <div
                style={{
                    border: `${ptToPx(bankingTable.border)}px solid ${bankingTable.borderColor}`,
                    borderRadius: ptToPx(bankingTable.radius),
                    overflow: 'hidden',
                    background: '#FFFFFF',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        background: bankingTable.headerBg,
                        minHeight: ptToPx(bankingTable.headerHeight),
                        borderBottom: `${ptToPx(bankingTable.rowDivider)}px solid ${bankingTable.rowDividerColor}`,
                    }}
                >
                    <ScreenHeaderCell flex={bankingTable.flex.bankName}>Bank Name</ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.accountNumber}>Account No.</ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.closingBalance} align="right">
                        {'Closing\nBalance'}
                    </ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.closingDate}>{'Closing Balance\nDate'}</ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.odLimit} align="right">
                        {'OD/CC Sanction\nLimit'}
                    </ScreenHeaderCell>
                    <ScreenHeaderCell flex={bankingTable.flex.ltmTxn} align="right">
                        {'Transactions\n(LTM)'}
                    </ScreenHeaderCell>
                </div>

                {/* Body */}
                {overview.accounts.map((a, i) => {
                    const closing = fmtINRSigned(a.closingBalance);
                    const od = fmtINRSigned(a.odSanctionLimit);
                    return (
                        <div
                            key={a.accountId ?? i}
                            style={{
                                display: 'flex',
                                background: bankingTable.rowBg,
                                minHeight: ptToPx(bankingTable.rowHeight),
                                borderBottom: `${ptToPx(bankingTable.rowDivider)}px solid ${bankingTable.rowDividerColor}`,
                            }}
                        >
                            <ScreenCell flex={bankingTable.flex.bankName} bold color={bankingTable.bankNameColor}>
                                {a.name ?? DASH}
                            </ScreenCell>
                            <ScreenCell flex={bankingTable.flex.accountNumber} mono>
                                {a.accountNumber ?? DASH}
                            </ScreenCell>
                            <ScreenCell
                                flex={bankingTable.flex.closingBalance}
                                align="right"
                                mono
                                color={closing.negative ? bankingTable.negColor : undefined}
                            >
                                {closing.text}
                            </ScreenCell>
                            <ScreenCell flex={bankingTable.flex.closingDate}>
                                {a.closingBalanceDate ? fmtDate(a.closingBalanceDate) : DASH}
                            </ScreenCell>
                            <ScreenCell
                                flex={bankingTable.flex.odLimit}
                                align="right"
                                mono
                                color={od.negative ? bankingTable.negColor : undefined}
                            >
                                {od.text}
                            </ScreenCell>
                            <ScreenCell flex={bankingTable.flex.ltmTxn} align="right" mono>
                                {fmtINR(a.transactionsLTM)}
                            </ScreenCell>
                        </div>
                    );
                })}

                {/* Footer */}
                <div
                    style={{
                        display: 'flex',
                        background: bankingTable.footerBg,
                        minHeight: ptToPx(bankingTable.headerHeight),
                        borderTop: `${ptToPx(bankingTable.footerBorderTop)}px solid ${bankingTable.footerBorderColor}`,
                    }}
                >
                    <ScreenCell flex={bankingTable.flex.bankName} bold color={bankingTable.footerColor}>
                        All Accounts
                    </ScreenCell>
                    <ScreenCell flex={bankingTable.flex.accountNumber} bold color={bankingTable.footerColor}>
                        {`${overview.accounts.length} Account${overview.accounts.length === 1 ? '' : 's'}`}
                    </ScreenCell>
                    <ScreenCell
                        flex={bankingTable.flex.closingBalance}
                        align="right"
                        mono
                        bold
                        color={totalsClosing.negative ? bankingTable.negColor : bankingTable.footerColor}
                    >
                        {totalsClosing.text}
                    </ScreenCell>
                    <ScreenCell flex={bankingTable.flex.closingDate}>{''}</ScreenCell>
                    <ScreenCell
                        flex={bankingTable.flex.odLimit}
                        align="right"
                        mono
                        bold
                        color={totalsOd.negative ? bankingTable.negColor : bankingTable.footerColor}
                    >
                        {totalsOd.text}
                    </ScreenCell>
                    <ScreenCell flex={bankingTable.flex.ltmTxn} align="right" mono bold color={bankingTable.footerColor}>
                        {fmtINR(totals.transactionsLTM)}
                    </ScreenCell>
                </div>
            </div>

            {/* Aggregated view — lives inside Banking (after the overview table), matching the AICA dashboard */}
            {data.aggBanking && data.aggBanking.mainTable.rows.length > 0 ? (
                <BankAnalysisBlock
                    title="Aggregated View"
                    subtitle={aggBankingSubtitle(data.aggBanking.accountCount)}
                    d={data.aggBanking}
                />
            ) : null}
            {accountsToPrint.length ? (
                <div {...{ [REPORT_ANCHOR_ATTR]: reportAnchorId(REPORT_MODULE_ID.bankingOverview, SUB_OPTION_INDIVIDUAL_BANKS_ID) }}>
                    {accountsToPrint.map((a) => (
                        <BankAnalysisBlock
                            key={a.accountNumber}
                            title={a.label}
                            d={a.body}
                            anchor={reportAnchorId(REPORT_MODULE_ID.bankingOverview, bankAccountSubId(a.accountNumber))}
                        />
                    ))}
                </div>
            ) : null}
        </ScreenSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                          M O D U L E   D E F
// ════════════════════════════════════════════════════════════════

export const bankingOverviewModule: ModuleDefinition = {
    meta: {
        id: 'bankingOverview',
        title: 'Banking Overview',
        description: 'Per-account closing balances, OD limits, LTM transactions and totals.',
        dataSource: 'polus/data/bankStatementData',
    },
    hasData: (d) => !!d.bankingOverview && d.bankingOverview.accounts.length > 0,
    Screen: ScreenBankingOverview,
};
